import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useUser } from 'util/UserContext'
import api from 'config/axiosConfig'
import SpillTheTeaModal from 'components/Posts/SpillTheTeaModal'
import AddButton from 'ui/AddButton'
import LoadingSkeleton from 'ui/LoadingSkeleton'
import NotFoundPage from 'components/ResponsePages/404NotFoundPage'
import { AxiosError } from 'axios'
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage'

import {
    Issue,
    Impact,
    Scope,
    Sort,
    SortField,
    SortOrder,
} from 'util/constants'
import { SelectTrigger, SelectValue, SelectItem, SelectContent, Select } from 'ui/InputSelect'
import ScrollHideContainer from 'ui/ScrollHideContainer'
import ImpactCardRefactored from './ImpactCardRefactored'
import EmptyState from 'ui/EmptyState'
import { apiRoutes } from "util/apiRoutes";

interface Props {
    sort: Sort
    entity: Issue
    entityType: 'issue'
}

const ImpactsSection = ({ entityType, entity }: Props) => {
    const { currentUser, setShowAuthModal } = useUser()
    const location = useLocation()
    const navigate = useNavigate()
    const [impacts, setImpacts] = useState<Impact[]>([])
    const [error, setError] = useState<string>('')
    const [openModal, setOpenModal] = useState<boolean>(false)
    const [scopeFilter, setScopeFilter] = useState<Scope | 'All'>('All')
    const [impactTypeFilter, setImpactTypeFilter] = useState<
        'All' | 'impact_negative' | 'impact_positive'
    >('All')
    const [sortField, setSortField] = useState<SortField>('created_at')
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
    const searchParams = new URLSearchParams(location.search)
    const sort = (searchParams.get('sort') as Sort) || 'new'

    useEffect(() => {
        switch (sort) {
            case 'top':
                setSortField('vote_count')
                setSortOrder('desc')
                break
            case 'bottom':
                setSortField('vote_count')
                setSortOrder('asc')
                break
            case 'new':
                setSortField('created_at')
                setSortOrder('desc')
                break
            case 'old':
                setSortField('created_at')
                setSortOrder('asc')
                break
            default:
                setSortField('created_at')
                setSortOrder('desc')
                break
        }
    }, [sort])

    useEffect(() => {
        const fetchImpacts = async () => {
            try {
                const response = await api.get<Impact[]>(
                    apiRoutes.posts.root, { params: { kind: "impact", issueId: entity.id } }
                )
                setImpacts(response.data)
            } catch (err) {
                if (err instanceof AxiosError && err.response?.status === 404) {
                    setError('404: Failed to load impacts. Please try again later.')
                } else {
                    setError('Failed to load impacts. Please try again later.')
                }
            }
        }

        if (entity) {
            fetchImpacts()
        }
    }, [entityType, entity])

    const handleImpactAdded = async () => {
        try {
            const response = await api.get<Impact[]>(
                apiRoutes.posts.root, { params: { kind: "impact", issueId: entity.id } }
            )
            setImpacts(response.data)
        } catch (err) {
            setError('Failed to refresh impacts. Please reload the page.')
        }
    }

    const handleSortChange = (newSort: Sort | 'updated') => {
        const currentParams = new URLSearchParams(location.search)
        
        switch (newSort) {
            case 'updated':
                // Toggle between asc/desc for updated_at
                setSortField('updated_at')
                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
                break
            case 'top':
                setSortField('vote_count')
                setSortOrder('desc')
                currentParams.set('sort', 'top')
                break
            case 'bottom':
                setSortField('vote_count')
                setSortOrder('asc')
                currentParams.set('sort', 'bottom')
                break
            case 'new':
                setSortField('created_at')
                setSortOrder('desc')
                currentParams.set('sort', 'new')
                break
            case 'old':
                setSortField('created_at')
                setSortOrder('asc')
                currentParams.set('sort', 'old')
                break
        }

        if (newSort !== 'updated') {
            navigate(`?${currentParams.toString()}`)
        }
    }

    const handleAddImpactButtonClick = () => {
        if (!currentUser) {
            setShowAuthModal(true)
        } else {
            setOpenModal(true)
        }
    }

    const handleCloseModal = () => {
        setOpenModal(false)
    }

    const filteredImpacts = impacts.filter((impact) => {
        const matchesScope =
            scopeFilter === 'All' || impact.properties?.scope === scopeFilter
        const matchesImpactType =
            impactTypeFilter === 'All' || impact.rel_type === impactTypeFilter
        return matchesScope && matchesImpactType
    })

    const sortedImpacts = [...filteredImpacts].sort((a, b) => {
        if (!sortField) return 0

        let aValue, bValue

        switch (sortField) {
            case 'vote_count':
                aValue = a.vote_count || 0
                bValue = b.vote_count || 0
                break
            case 'created_at':
                aValue = new Date(a.created_at || 0).getTime()
                bValue = new Date(b.created_at || 0).getTime()
                break
            case 'updated_at':
                aValue = new Date(a.updated_at || 0).getTime()
                bValue = new Date(b.updated_at || 0).getTime()
                break
            default:
                return 0
        }

        if (sortOrder === 'asc') {
            return aValue - bValue
        } else {
            return bValue - aValue
        }
    })

    if (error) {
        return error.match(/404/) ? <NotFoundPage displayStyle={`page`} errorMessage={`Sorry, we couldn't find any impacts for this issue.`} buttonText={`View Other Impacts`} buttonHref={`/impacts/new`} /> : <ServerErrorPage />
    }

    if (!impacts) {
        return (
            <LoadingSkeleton variant="entity-card" count={6} />
        )
    }

    return (
        <div className="impacts-section">
            <ScrollHideContainer 
                className="z-50 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg"
                hideOffset={115}
                threshold={114}
            >
                <div className="flex flex-col space-y-1 mb-1">
                    <div className="flex mb-1 mx-1">
                        <AddButton
                            tooltipTitle="Add Impact"
                            buttonText="Impact"
                            className="mx-2 w-full"
                            onClick={handleAddImpactButtonClick}
                        />
                    </div>
                    <div className="flex mx-3 gap-1 pb-1">
                        <Select onValueChange={(value: Sort) => handleSortChange(value)}>
                            <SelectTrigger>
                                <SelectValue placeholder="Sort" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="top">Top</SelectItem>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="bottom">Bottom</SelectItem>
                                <SelectItem value="old">Old</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select onValueChange={(value) => setScopeFilter(value as Scope)}>
                            <SelectTrigger>
                                <SelectValue placeholder="Scope" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="All">Any Scope</SelectItem>
                                <SelectItem value="Local">Local</SelectItem>
                                <SelectItem value="National">National</SelectItem>
                                <SelectItem value="Global">Global</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select onValueChange={(value) => setImpactTypeFilter(value as 'All' | 'impact_negative' | 'impact_positive')}>
                            <SelectTrigger>
                                <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="All">Any Type</SelectItem>
                                <SelectItem value="impact_negative">Negative</SelectItem>
                                <SelectItem value="impact_positive">Positive</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </ScrollHideContainer>
            <div className="mt-2">
                {impacts.length === 0 ? (
                    <EmptyState 
                        title="No impacts yet"
                        subtitle="Be the first to add an impact for this issue!"
                        className="m-4"
                    />
                ) : (
                    <div className="flex flex-col divide-y divide-eoe-border-main dark:divide-eoe-dark-border-main px-2">
                        {sortedImpacts.map((impact) => (
                            <ImpactCardRefactored
                                key={impact.id}
                                impact={impact}
                                truncateDescription={true}
                                isEntitySection={true}
                            />
                        ))}
                    </div>
                )}
            </div>
            {openModal && (
                <SpillTheTeaModal
                    open={openModal}
                    onClose={handleCloseModal}
                    onImpactAdded={handleImpactAdded}
                    initialSelectedIssue={entity}
                    customTitle="Add an impact"
                    fixedPostType="impact"
                    hidePostTypeButtons={true}
                />
            )}
        </div>
    )
}

export default ImpactsSection